'use client';
import styles from './autocomplete.module.css';
import useOnclickOutside from 'react-cool-onclickoutside';
import { ChangeEvent, useEffect, useState, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	faLocationDot,
	faX,
} from '@awesome.me/kit-012d1b9af0/icons/classic/regular';
import { useProperties } from '@/context/properties-context';
import { UserDataType } from '@/types/user';
import { autocomplete, geocodeAddress } from '@/lib/google-place';

type ProfileLocationProps =
	| {
			toProfile: true;
			userLocation: UserDataType['location'];
			userSetLocation: React.Dispatch<
				React.SetStateAction<UserDataType['location']>
			>;
	  }
	| {
			toProfile?: false;
			userLocation?: undefined;
			userSetLocation?: undefined;
	  };

type PlacesAutocompleteProps = {
	label?: boolean;
	placeholder?: string;
	className?: string;
} & ProfileLocationProps;

interface AutocompletePrediction {
	place_id: string;
	description: string;
	structured_formatting: {
		main_text: string;
		secondary_text: string;
	};
}

const PlacesAutocomplete = ({
	label = true,
	placeholder = 'Enter Region',
	className,
	toProfile,
	userLocation,
	userSetLocation,
}: PlacesAutocompleteProps) => {
	const { properties, updateProperty } = useProperties();

	const [value, setValue] = useState(
		toProfile
			? userLocation?.formatted_address ?? ''
			: properties.location.name ?? ''
	);

	const [suggestions, setSuggestions] = useState<AutocompletePrediction[]>([]);
	const [showSuggestions, setShowSuggestions] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const debounceRef = useRef<NodeJS.Timeout | null>(null);

	const ref = useOnclickOutside(() => {
		setShowSuggestions(false);
	});

	const handleInput = async (e: ChangeEvent<HTMLInputElement>) => {
		const inputValue = e.target.value;
		setValue(inputValue);

		// Clear previous debounce
		if (debounceRef.current) {
			clearTimeout(debounceRef.current);
		}

		// Debounce autocomplete requests
		debounceRef.current = setTimeout(async () => {
			if (inputValue.length > 2) {
				setIsLoading(true);
				try {
					const results = await autocomplete(inputValue);
					setSuggestions(results);
					setShowSuggestions(true);
				} catch (error) {
					console.error('Autocomplete error:', error);
					setSuggestions([]);
				} finally {
					setIsLoading(false);
				}
			} else {
				setSuggestions([]);
				setShowSuggestions(false);
			}
		}, 300);
	};

	const handleSelect = (suggestion: AutocompletePrediction) => async () => {
		// Set the selected prediction text
		setValue(suggestion.description);
		setShowSuggestions(false);
		setSuggestions([]);
		setIsLoading(true);

		try {
			// Geocode the selected address to get coordinates and full details
			const geocoded = await geocodeAddress(suggestion.description);

			if (geocoded) {
				if (toProfile) {
					userSetLocation({
						formatted_address: geocoded.formatted_address,
						address_components: geocoded.address_components,
						lat: geocoded.lat,
						lng: geocoded.lng,
					});
				} else {
					updateProperty('location', {
						name: geocoded.formatted_address,
						lat: geocoded.lat,
						lng: geocoded.lng,
					});
					updateProperty('sortBy', 'distance');
				}
				// Update input with properly formatted address from geocode
				setValue(geocoded.formatted_address);
			}
		} catch (error) {
			console.error('Geocoding error:', error);
		} finally {
			setIsLoading(false);
		}
	};

	const renderSuggestions = () =>
		suggestions.map((suggestion, index) => {
			const {
				place_id,
				structured_formatting: { main_text, secondary_text },
			} = suggestion;

			return (
				<li
					key={place_id}
					onClick={handleSelect(suggestion)}
					style={{
						borderBottom:
							index !== suggestions.length - 1 ? '1px solid #446cb3' : 'none',
					}}
				>
					<FontAwesomeIcon icon={faLocationDot} color='#446cb3' />{' '}
					<strong>{main_text}</strong> <small>{secondary_text}</small>
				</li>
			);
		});

	const clearInput = () => {
		setValue('');
		setSuggestions([]);
		setShowSuggestions(false);

		if (toProfile) {
			userSetLocation({
				formatted_address: '',
				address_components: [],
				lat: 0,
				lng: 0,
			});
		} else {
			updateProperty('location', { name: '', lat: 0, lng: 0 });
			updateProperty('sortBy', 'lastUpdate');
		}
	};

	useEffect(() => {
		if (!toProfile && properties.location.name !== value) {
			setValue(properties.location.name);
		}
	}, [toProfile, properties.location.name]);

	// Cleanup debounce on unmount
	useEffect(() => {
		return () => {
			if (debounceRef.current) {
				clearTimeout(debounceRef.current);
			}
		};
	}, []);

	return (
		<div
			ref={ref}
			className={`${className ? className : styles['location-container']}`}
		>
			{label && <label htmlFor='name'>Location</label>}
			<input
				type='text'
				id='name'
				value={value}
				onChange={handleInput}
				placeholder={placeholder}
				onFocus={() => {
					if (suggestions.length > 0) {
						setShowSuggestions(true);
					}
				}}
			/>
			{value && (
				<FontAwesomeIcon
					icon={faX}
					size='lg'
					className={`${!!label && styles.clear}`}
					color={!!!label ? '#446cb3' : '#fff'}
					onClick={clearInput}
				/>
			)}
			{isLoading && <div className={styles.loading}>Loading...</div>}
			{showSuggestions && suggestions.length > 0 && (
				<ul>{renderSuggestions()}</ul>
			)}
		</div>
	);
};

export default PlacesAutocomplete;
